import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, Phone, MessageSquare, Mail, 
  Clock, Building2, 
  Plus, MoreVertical,
  FileText, History, Trash2,
  Edit3, CheckCircle2, Save, Smile, Meh, Frown, X, Loader2
} from 'lucide-react';
import { leadService } from '../../../services/erpApi';
import Avatar from '../../../components/Avatar/Avatar';
import './LeadDetailCommunicationsPage.css';

interface Lead {
  id: string;
  name: string;
  institution: string;
  specialty: string;
  email: string;
  phone: string;
  cellphone?: string;
  status: string;
  temperature: 'cold' | 'warm' | 'hot';
  avatar_url?: string;
}

interface Communication {
  id: string;
  type: 'phone' | 'message' | 'email';
  interaction_date: string;
  content: string;
  subject?: string;
  created_by_name: string;
  created_by_avatar?: string;
  status: 'completed' | 'pending';
}

const LeadDetailCommunicationsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'all' | 'calls' | 'messages' | 'emails'>('all');
  const [lead, setLead] = useState<Lead | null>(null);
  const [communications, setCommunications] = useState<Communication[]>([]);
  const [loading, setLoading] = useState(true);
  const [callOpen, setCallOpen] = useState(false);
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [interactionOpen, setInteractionOpen] = useState(false);
  const [interactionType, setInteractionType] = useState<'phone' | 'message' | 'email'>('phone');
  const [interactionNote, setInteractionNote] = useState('');
  const [satisfaction, setSatisfaction] = useState<'positive' | 'neutral' | 'negative' | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const loadLeadData = useCallback(async () => {
    if (!id) return;
    try {
      const response = await leadService.getById(id);
      if (response.success) {
        setLead(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar lead:', error);
    }
  }, [id]);

  const loadCommunications = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await leadService.getInteractions(id);
      if (response.success) {
        setCommunications(response.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar comunicações:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadLeadData();
    loadCommunications();
  }, [loadLeadData, loadCommunications]);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'phone': return <Phone size={18} />;
      case 'message': return <MessageSquare size={18} />;
      case 'email': return <Mail size={18} />;
      default: return <FileText size={18} />;
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'phone': return 'Registro de Ligação';
      case 'message': return 'Contato via WhatsApp';
      case 'email': return 'E-mail Enviado';
      default: return 'Interação';
    }
  };

  const filteredCommunications = communications.filter(comm => {
    if (activeTab === 'all') return true;
    if (activeTab === 'calls') return comm.type === 'phone';
    if (activeTab === 'messages') return comm.type === 'message';
    if (activeTab === 'emails') return comm.type === 'email';
    return true;
  });

  const phones = [
    lead?.phone ? { label: 'Telefone', value: lead.phone } : null,
    lead?.cellphone ? { label: 'Celular', value: lead.cellphone } : null
  ].filter((item): item is { label: string; value: string } => Boolean(item));

  const whatsappUrl = (value: string) => {
    const digits = value.replace(/\D/g, '');
    const international = digits.startsWith('55') ? digits : `55${digits}`;
    return `https://wa.me/${international}`;
  };

  const openWhatsapp = (value: string) => {
    window.open(whatsappUrl(value), '_blank', 'noopener,noreferrer');
    setWhatsappOpen(false);
  };

  const handleWhatsapp = () => {
    setCallOpen(false);
    if (phones.length === 1) {
      openWhatsapp(phones[0].value);
      return;
    }
    setWhatsappOpen((open) => !open);
  };

  const openLog = (type: 'phone' | 'message' | 'email') => {
    setInteractionType(type);
    setInteractionNote('');
    setSatisfaction(null);
    setSaveSuccess(false);
    setInteractionOpen(true);
  };

  const handleSaveInteraction = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!id || !interactionNote.trim()) return;
    try {
      setIsSaving(true);
      const response = await leadService.createInteraction(id, {
        type: interactionType,
        content: interactionNote.trim(),
        satisfaction: satisfaction || null,
        subject: null,
        next_action: null
      });
      if (!response.success) throw new Error(response.message || 'Erro ao salvar interação');
      setSaveSuccess(true);
      setInteractionNote('');
      setSatisfaction(null);
      await loadCommunications();
      setTimeout(() => {
        setInteractionOpen(false);
        setSaveSuccess(false);
      }, 1200);
    } catch (error: any) {
      alert(error.response?.data?.message || error.message || 'Erro ao salvar interação');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading && !lead) {
    return (
      <div className="lead-detail-communications-page">
        <div className="lead-detail-loading">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="lead-detail-communications-page">
      {/* Header */}
      <div className="lead-detail-header">
        <div className="lead-detail-header-left">
          <button 
            onClick={() => navigate('/erp/prospecting')}
            className="lead-detail-back-btn"
          >
            <ChevronLeft size={20} />
          </button>
          <div>
            <h1 className="lead-detail-title">
              Histórico do <span className="lead-detail-title-accent">Lead</span>
            </h1>
            <p className="lead-detail-subtitle">Análise Detalhada de Interações</p>
          </div>
        </div>
        <div className="lead-detail-header-actions">
          <button className="lead-detail-btn-secondary">
            <Edit3 size={16} /> Editar Lead
          </button>
          <button type="button" className="lead-detail-btn-primary" onClick={() => openLog('phone')}>
            <Plus size={16} /> Nova Interação
          </button>
        </div>
      </div>

      <div className="lead-detail-grid">
        {/* Left Column: Lead Profile */}
        <div className="lead-detail-profile-column">
          <div className="lead-detail-profile-card">
            <div className="lead-detail-profile-header">
              <div className="lead-detail-avatar-wrapper">
                <Avatar 
                  name={lead?.name || ''} 
                  email=""
                  imageUrl={lead?.avatar_url} 
                  size="xl" 
                />
                {lead?.temperature === 'hot' && (
                  <div className="lead-detail-temperature-badge lead-detail-temperature-hot" title="Lead Quente" />
                )}
                {lead?.temperature === 'warm' && (
                  <div className="lead-detail-temperature-badge lead-detail-temperature-warm" title="Lead Morno" />
                )}
                {lead?.temperature === 'cold' && (
                  <div className="lead-detail-temperature-badge lead-detail-temperature-cold" title="Lead Frio" />
                )}
              </div>
              <h2 className="lead-detail-profile-name">{lead?.name}</h2>
              <p className="lead-detail-profile-specialty">{lead?.specialty}</p>
            </div>

            <div className="lead-detail-profile-info">
              <div className="lead-detail-info-item">
                <div className="lead-detail-info-icon">
                  <Building2 size={16} />
                </div>
                <div className="lead-detail-info-content">
                  <span className="lead-detail-info-label">Instituição</span>
                  <span className="lead-detail-info-value">{lead?.institution}</span>
                </div>
              </div>
              <div className="lead-detail-info-item">
                <div className="lead-detail-info-icon">
                  <Mail size={16} />
                </div>
                <div className="lead-detail-info-content">
                  <span className="lead-detail-info-label">E-mail</span>
                  <span className="lead-detail-info-value">{lead?.email}</span>
                </div>
              </div>
              <div className="lead-detail-info-item">
                <div className="lead-detail-info-icon">
                  <Phone size={16} />
                </div>
                <div className="lead-detail-info-content">
                  <span className="lead-detail-info-label">Telefone</span>
                  <span className="lead-detail-info-value">{lead?.phone}</span>
                </div>
              </div>
            </div>

            <div className="lead-detail-status-card">
              <div className="lead-detail-status-header">
                <span className="lead-detail-status-label">Status Atual</span>
                <CheckCircle2 size={14} />
              </div>
              <p className="lead-detail-status-value">{lead?.status}</p>
            </div>
          </div>

          <div className="lead-detail-quick-actions">
            <h3 className="lead-detail-quick-actions-title">Ações Rápidas</h3>
            <div className="lead-detail-quick-actions-grid">
              <button type="button" className={`lead-detail-quick-action-btn ${callOpen ? 'is-on' : ''}`} onClick={() => { setWhatsappOpen(false); setCallOpen((open) => !open); }}>
                <Phone size={20} />
                <span>Ligar</span>
              </button>
              <button type="button" className={`lead-detail-quick-action-btn ${whatsappOpen ? 'is-on' : ''}`} onClick={handleWhatsapp}>
                <MessageSquare size={20} />
                <span>WhatsApp</span>
              </button>
              <button type="button" className="lead-detail-quick-action-btn" onClick={() => openLog('phone')}>
                <Phone size={20} />
                <span>Lançar ligação</span>
              </button>
              <button type="button" className="lead-detail-quick-action-btn" onClick={() => openLog('message')}>
                <MessageSquare size={20} />
                <span>Lançar WhatsApp</span>
              </button>
              <button type="button" className="lead-detail-quick-action-btn" onClick={() => openLog('email')}>
                <Mail size={20} />
                <span>Lançar e-mail</span>
              </button>
            </div>
            {(callOpen || (whatsappOpen && phones.length !== 1)) && (
              <div className="lead-detail-phone-list">
                {phones.length === 0 ? (
                  <p>Nenhum telefone cadastrado.</p>
                ) : phones.map((phone) => (
                  whatsappOpen ? (
                    <button key={phone.label} type="button" onClick={() => openWhatsapp(phone.value)}>
                      {phone.label} · {phone.value}
                    </button>
                  ) : (
                    <a key={phone.label} href={`tel:${phone.value.replace(/\D/g, '')}`}>
                      {phone.label} · {phone.value}
                    </a>
                  )
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Communications Timeline */}
        <div className="lead-detail-communications-column">
          {/* Tabs */}
          <div className="lead-detail-tabs">
            {[
              { id: 'all', label: 'Todas', icon: History },
              { id: 'calls', label: 'Ligações', icon: Phone },
              { id: 'messages', label: 'Mensagens', icon: MessageSquare },
              { id: 'emails', label: 'E-mails', icon: Mail },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`lead-detail-tab ${activeTab === tab.id ? 'lead-detail-tab-active' : ''}`}
              >
                <tab.icon size={14} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Timeline */}
          <div className="lead-detail-timeline">
            {filteredCommunications.length === 0 ? (
              <div className="lead-detail-empty">
                <p>Nenhuma comunicação encontrada</p>
              </div>
            ) : (
              filteredCommunications.map((comm) => (
                <div key={comm.id} className="lead-detail-communication-card">
                  <div className="lead-detail-communication-header">
                    <div className="lead-detail-communication-icon-wrapper">
                      <div className="lead-detail-communication-icon">
                        {getTypeIcon(comm.type)}
                      </div>
                    </div>
                    <div className="lead-detail-communication-title-wrapper">
                      <h4 className="lead-detail-communication-title">
                        {getTypeLabel(comm.type)}
                      </h4>
                      <div className="lead-detail-communication-date">
                        <Clock size={12} /> {formatDate(comm.interaction_date)}
                      </div>
                    </div>
                    <button className="lead-detail-communication-menu-btn">
                      <MoreVertical size={18} />
                    </button>
                  </div>

                  <div className="lead-detail-communication-content">
                    <p className="lead-detail-communication-text">
                      "{comm.content}"
                    </p>
                  </div>

                  <div className="lead-detail-communication-footer">
                    <div className="lead-detail-communication-responsible">
                      <Avatar
                        name={comm.created_by_name}
                        email=""
                        imageUrl={comm.created_by_avatar}
                        size="sm"
                      />
                      <div className="lead-detail-communication-responsible-info">
                        <span className="lead-detail-communication-responsible-label">Registrado por</span>
                        <span className="lead-detail-communication-responsible-name">{comm.created_by_name}</span>
                      </div>
                    </div>
                    <div className="lead-detail-communication-actions">
                      <button className="lead-detail-communication-action-btn" title="Editar Registro">
                        <Edit3 size={16} />
                      </button>
                      <button className="lead-detail-communication-action-btn" title="Excluir Registro">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}

            {filteredCommunications.length > 0 && (
              <div className="lead-detail-load-more">
                <button className="lead-detail-load-more-btn">
                  Carregar Histórico Anterior
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {interactionOpen && (
        <div className="lead-detail-modal-overlay">
          <button type="button" className="lead-detail-modal-backdrop" aria-label="Fechar" onClick={() => !isSaving && setInteractionOpen(false)} />
          <div className="lead-detail-modal">
            {saveSuccess ? (
              <div className="lead-detail-modal-success">
                <CheckCircle2 size={36} />
                <h3>Registro concluído</h3>
                <p>O histórico deste lead foi atualizado.</p>
              </div>
            ) : (
              <form onSubmit={handleSaveInteraction}>
                <div className="lead-detail-modal-head">
                  <div>
                    <h3>Registrar contato</h3>
                    <p>{interactionType === 'phone' ? 'Ligação' : interactionType === 'message' ? 'WhatsApp' : 'E-mail'} com {lead?.name}</p>
                  </div>
                  <button type="button" onClick={() => setInteractionOpen(false)} aria-label="Fechar"><X size={20} /></button>
                </div>
                <label>
                  Descrição da conversa
                  <textarea required rows={4} value={interactionNote} onChange={(event) => setInteractionNote(event.target.value)} placeholder="Como foi o contato e qual o próximo passo?" />
                </label>
                <div className="lead-detail-satisfaction">
                  <button type="button" className={satisfaction === 'positive' ? 'is-on' : ''} onClick={() => setSatisfaction('positive')} aria-label="Positiva"><Smile size={20} /></button>
                  <button type="button" className={satisfaction === 'neutral' ? 'is-on' : ''} onClick={() => setSatisfaction('neutral')} aria-label="Neutra"><Meh size={20} /></button>
                  <button type="button" className={satisfaction === 'negative' ? 'is-on' : ''} onClick={() => setSatisfaction('negative')} aria-label="Negativa"><Frown size={20} /></button>
                </div>
                <button type="submit" className="lead-detail-btn-primary" disabled={isSaving}>
                  {isSaving ? <Loader2 size={16} /> : <Save size={16} />} Salvar registro
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadDetailCommunicationsPage;
